export type PartialRecordUnionType = "OR" | "AND";

function isDefinedPartialField<T extends object>(record: T | undefined, key: keyof T & string): boolean {
  return record !== undefined && Object.prototype.hasOwnProperty.call(record, key) && record[key] !== undefined;
}

export function mergePartialField<T extends object, K extends keyof T & string>(
  result: T,
  mergeIn: T | undefined,
  key: K,
  combine: (left: T[K], right: T[K]) => T[K],
): void {
  const resultDefined = isDefinedPartialField(result, key);
  const mergeInDefined = isDefinedPartialField(mergeIn, key);

  if (!resultDefined && !mergeInDefined) {
    return;
  }
  if (!resultDefined) {
    if (mergeIn !== undefined) {
      result[key] = mergeIn[key];
    }
    return;
  }
  if (!mergeInDefined) {
    return;
  }
  if (mergeIn !== undefined) {
    result[key] = combine(result[key], mergeIn[key]);
  }
}

export function mergePartialBooleanField<T extends object>(
  result: T,
  mergeIn: T | undefined,
  key: keyof T & string,
  union: PartialRecordUnionType = "OR",
): void {
  mergePartialField(result, mergeIn, key, (left, right) => {
    if (union === "OR") {
      return (left || right) as T[keyof T & string];
    }
    return (left && right) as T[keyof T & string];
  });
}

export function mergePartialMinNumberField<T extends object>(
  result: T,
  mergeIn: T | undefined,
  key: keyof T & string,
): void {
  mergePartialField(result, mergeIn, key, (left, right) => {
    return Math.min(left as number, right as number) as T[keyof T & string];
  });
}

export function mergePartialTruthyField<T extends object>(
  result: T,
  mergeIn: T | undefined,
  key: keyof T & string,
): void {
  mergePartialField(result, mergeIn, key, (left, right) => {
    return (left || right) as T[keyof T & string];
  });
}
